// IEA photos, shared by Collect and Photos mode. Words: IEA_PHOTO_FLOW_WORDS.md (Robert 2026-10-16).
// * Pictures: from this iPad first (works offline), else from the cloud (admin read, storage rule 3).
// * Upload state: the small cloud mark until a photo is sent.
// * The big view: Done, the label (tap to change), Share, Retake, trash can. iPhone words throughout.
// * Delete: confirm, then Undo for 6 seconds. Only after that is the delete saved.
// Actions use data-pv so they work in Collect and in Photos.

import { surveyPhotoUrl } from "./firebase";
import { deviceList } from "./outbox";
import { photoDate } from "./survey-collect";
import { esc } from "./training-screens";

export type PhotoSrc = { state: "ok"; src: string } | { state: "loading" } | { state: "error" };
export type Upload = "waiting" | "sent" | null;

/** One photo in the big view. Target is where it lives: A:asset or B:housing:breaker. */
export interface ViewItem {
  path: string;
  type: string;
  target: string;
  /** What the big view calls it: "Front", or the row "B4". */
  label: string;
}

/** What Collect lends the photo store. */
export interface PhotoHooks {
  paint: () => void;
  /** Photos taken for a target, in the order the card shows them. */
  items: (target: string) => ViewItem[];
  /** Labels this photo may change to. Empty for a row photo. */
  labels: (target: string) => string[];
  /** Title for the big view: "MSD-01" or "MCC-1A · B4". */
  title: (target: string) => string;
  retake: (target: string, type: string) => void;
  /** Hide now (Undo still possible). */
  hide: (target: string, type: string) => void;
  restore: (target: string, type: string, path: string) => void;
  /** Save the delete: record cleared, Deleted Photos line, unsent file off the iPad. */
  commitDelete: (target: string, type: string, path: string) => Promise<void>;
  relabel: (target: string, from: string, to: string) => Promise<void>;
}

let hooks: PhotoHooks | null = null;
const urls = new Map<string, PhotoSrc>();
const uploads = new Map<string, Upload>();
let paintSoon: ReturnType<typeof setTimeout> | null = null;
let lastStateRead = 0;

let view: { target: string; i: number } | null = null;
let confirm = false;
let undo: { target: string; type: string; path: string; label: string; timer: ReturnType<typeof setTimeout> } | null = null;
let note = "";
let noteTimer: ReturnType<typeof setTimeout> | null = null;

export const UNDO_MS = 6000;

export function photoStoreInit(h: PhotoHooks): void {
  hooks = h;
}

function repaint(): void {
  if (paintSoon) return;
  paintSoon = setTimeout(() => {
    paintSoon = null;
    hooks?.paint();
  }, 60);
}

async function fetchSrc(path: string): Promise<void> {
  let src: PhotoSrc = { state: "error" };
  try {
    const local = await deviceList.photo(path);
    if (local) {
      src = { state: "ok", src: URL.createObjectURL(local.blob) };
      uploads.set(path, local.state === "sent" ? "sent" : "waiting");
    } else {
      src = { state: "ok", src: await surveyPhotoUrl(path) };
      uploads.set(path, "sent");
    }
  } catch {
    // Not on this iPad and no signal (or no admin read). The tile says so.
  }
  urls.set(path, src);
  repaint();
}

/** The picture for a photo. Starts loading on first ask. */
export function srcOf(path: string): PhotoSrc {
  const u = urls.get(path);
  if (u) return u;
  urls.set(path, { state: "loading" });
  void fetchSrc(path);
  return { state: "loading" };
}

/** Waiting to upload, uploaded, or not known yet. Checked again every few seconds while shown. */
export function uploadOf(path: string): Upload {
  if (Date.now() - lastStateRead > 4000) {
    lastStateRead = Date.now();
    void refreshUploads();
  }
  return uploads.get(path) ?? null;
}

async function refreshUploads(): Promise<void> {
  let changed = false;
  try {
    for (const p of await deviceList.photos()) {
      const st: Upload = p.state === "sent" ? "sent" : "waiting";
      if (uploads.get(p.path) !== st) {
        uploads.set(p.path, st);
        changed = true;
      }
    }
  } catch {
    // No device list. Marks stay as they are.
  }
  if (changed) repaint();
}

/** Try a photo again after it could not load. */
export function retry(path: string): void {
  urls.delete(path);
  hooks?.paint();
}

// ---- The big view ----

export function open(target: string, type: string): void {
  if (!hooks) return;
  const list = hooks.items(target);
  const i = Math.max(0, list.findIndex((x) => x.type === type));
  if (!list.length) return;
  view = { target, i };
  confirm = false;
  hooks.paint();
}

export function isOpen(): boolean {
  return Boolean(view);
}

function cur(): ViewItem | null {
  if (!view || !hooks) return null;
  return hooks.items(view.target)[view.i] ?? null;
}

export function step(d: number): void {
  if (!view || !hooks) return;
  const n = view.i + d;
  if (n < 0 || n >= hooks.items(view.target).length) return;
  view.i = n;
  hooks.paint();
}

export function done(): void {
  view = null;
  confirm = false;
  hooks?.paint();
}

export function retake(): void {
  const p = cur();
  if (!p || !hooks) return;
  view = null;
  confirm = false;
  hooks.paint();
  hooks.retake(p.target, p.type);
}

export function askDelete(): void {
  if (!cur()) return;
  confirm = true;
  hooks?.paint();
}

export function cancelDelete(): void {
  confirm = false;
  hooks?.paint();
}

/** Delete Photo: hide it now, save the delete after the Undo time runs out. */
export function confirmDelete(): void {
  const p = cur();
  if (!p || !hooks) return;
  flushUndo();
  hooks.hide(p.target, p.type);
  const h = hooks;
  undo = {
    target: p.target,
    type: p.type,
    path: p.path,
    label: p.label,
    timer: setTimeout(() => {
      const u = undo;
      undo = null;
      if (u) void h.commitDelete(u.target, u.type, u.path).then(() => h.paint());
      h.paint();
    }, UNDO_MS),
  };
  confirm = false;
  if (view && !h.items(view.target).length) view = null;
  else if (view) view.i = Math.min(view.i, h.items(view.target).length - 1);
  h.paint();
}

/** A second delete inside the Undo time saves the first one at once. */
function flushUndo(): void {
  if (!undo || !hooks) return;
  clearTimeout(undo.timer);
  void hooks.commitDelete(undo.target, undo.type, undo.path);
  undo = null;
}

export function undoDelete(): void {
  if (!undo || !hooks) return;
  clearTimeout(undo.timer);
  hooks.restore(undo.target, undo.type, undo.path);
  undo = null;
  say("Photo restored.");
}

/** Leaving the screen saves any delete still waiting on Undo. */
export function settle(): void {
  flushUndo();
}

export async function relabel(to: string): Promise<void> {
  const p = cur();
  if (!p || !hooks || p.type === to) return;
  await hooks.relabel(p.target, p.type, to);
  if (view) view.i = Math.max(0, hooks.items(view.target).findIndex((x) => x.type === to));
  say(`Now labeled ${to}.`);
}

/** Share: the iPad Share sheet with the photo (Save Image, AirDrop, Mail). */
export async function share(): Promise<void> {
  const p = cur();
  if (!p) return;
  const name = p.path.split("/").pop() ?? "photo.jpg";
  try {
    let blob: Blob | null = (await deviceList.photo(p.path))?.blob ?? null;
    if (!blob) {
      const s = srcOf(p.path);
      if (s.state === "ok") blob = await (await fetch(s.src)).blob();
    }
    const file = blob ? new File([blob], name, { type: blob.type || "image/jpeg" }) : null;
    if (file && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: name });
      return;
    }
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return;
  }
  // No Share sheet here: open the photo on its own, where a long press saves it.
  const s = srcOf(p.path);
  if (s.state === "ok") window.open(s.src, "_blank", "noopener");
  else say("Share needs the photo loaded first.");
}

function say(text: string): void {
  note = text;
  if (noteTimer) clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    note = "";
    hooks?.paint();
  }, 1800);
  hooks?.paint();
}

// ---- HTML ----

/** The cloud mark on a thumbnail until it uploads. */
export function uploadMark(path: string): string {
  return uploadOf(path) === "waiting" ? '<span class="pv-wait" title="Waiting to upload" aria-label="Waiting to upload"><svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true"><path d="M4 11h8a3 3 0 0 0 .4-6A4.5 4.5 0 0 0 3.6 5.6 2.7 2.7 0 0 0 4 11z" fill="none" stroke="#fff" stroke-width="1.4"/><path d="M8 9V5.5M6.4 7 8 5.4 9.6 7" fill="none" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/></svg></span>' : "";
}

/** A thumbnail picture for a tile, or words while it loads. */
export function thumb(path: string, alt: string): string {
  const s = srcOf(path);
  if (s.state === "ok") return `<img src="${esc(s.src)}" alt="${esc(alt)}" loading="lazy">`;
  return `<span class="pv-load">${s.state === "loading" ? "Loading" : "No signal"}</span>`;
}

const TRASH = '<svg width="20" height="22" viewBox="0 0 20 22" aria-hidden="true"><path d="M2 5h16M7 5V2.5h6V5M4 5l1 15h10l1-15M8 9v8M12 9v8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const SHARE = '<svg width="18" height="22" viewBox="0 0 18 22" aria-hidden="true"><path d="M9 14V2M5 6l4-4 4 4M5 9H3v11h12V9h-2" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** The big view, its Delete sheet, and the Undo toast. Empty when none is showing. */
export function storeSheet(): string {
  let h = "";
  const p = cur();
  if (view && p && hooks) {
    const list = hooks.items(view.target);
    const labels = hooks.labels(p.target);
    const s = srcOf(p.path);
    const pic = s.state === "ok" ? `<img src="${esc(s.src)}" alt="${esc(p.label)} photo">` : `<span class="pv-load">${s.state === "loading" ? "Loading" : "No signal. Tap Done and try again."}</span>`;
    const label = labels.length > 1 ? `<label class="pv-label"><span class="sr">Label</span><select data-pv-label="1">${labels.map((t) => `<option${t === p.type ? " selected" : ""}>${esc(t)}</option>`).join("")}</select></label>` : `<b class="pv-label">${esc(p.label)}</b>`;
    const up = uploadOf(p.path);
    h += `<div class="pv-view" role="dialog" aria-label="Photo">
      <div class="pv-hd"><button class="pv-btn" type="button" data-pv="done">Done</button><div class="pv-mid"><small>${esc(hooks.title(p.target))}</small>${label}</div><button class="pv-btn icon" type="button" data-pv="share" aria-label="Share">${SHARE}</button></div>
      <div class="pv-stage"><button class="pv-arrow" type="button" data-pv="prev" aria-label="Previous photo"${view.i === 0 ? " disabled" : ""}>‹</button><div class="pv-big" id="pv-big">${pic}</div><button class="pv-arrow" type="button" data-pv="next" aria-label="Next photo"${view.i >= list.length - 1 ? " disabled" : ""}>›</button></div>
      <div class="pv-cap"><span>${view.i + 1} of ${list.length}</span><span>Taken ${esc(photoDate(p.path))}</span><span>${up === "waiting" ? "Waiting to upload" : up === "sent" ? "Uploaded" : ""}</span></div>
      <div class="pv-ft"><button class="pv-btn" type="button" data-pv="retake">Retake</button><span class="pv-grow"></span><button class="pv-btn icon red" type="button" data-pv="trash" aria-label="Delete">${TRASH}</button></div>
    </div>`;
    if (confirm) {
      h += `<div class="pv-confirm" role="alertdialog" aria-label="Delete this photo"><div class="pv-box"><b>Delete this photo?</b><p>${up === "sent" ? "It comes off this asset. A copy stays out of sight for the record, like Recently Deleted." : "It has not uploaded yet. It is deleted from this iPad and never uploads."}</p><button class="pv-del" type="button" data-pv="del-yes">Delete Photo</button><button class="pv-cancel" type="button" data-pv="del-no">Cancel</button></div></div>`;
    }
  }
  if (undo) h += `<div class="pv-toast" role="status"><span>${esc(undo.label)} photo deleted.</span><button type="button" data-pv="undo">Undo</button></div>`;
  else if (note) h += `<div class="pv-toast" role="status"><span>${esc(note)}</span></div>`;
  return h;
}
